import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { apiRequest, msalInstance, silentAuthRedirectUri } from "./msalConfig";
import { decodeJwtPayload } from "./decodeJwt";
import { extractRoles, type AdminRole } from "./roles";

/**
 * Roles del principal firmado, para gating de UI (bug real encontrado en
 * produccion: el panel no mostraba crear/editar/eliminar plantillas, ni
 * ninguna otra accion de Operador, para NINGUN usuario). Se leen del
 * ACCESS TOKEN del Admin API (mismo recurso que `api/client.ts` usa para
 * llamar al backend), nunca del ID token: los appRoles y sus asignaciones
 * viven en la app registration "Panel Admin API", no en "Panel Admin SPA"
 * -- el ID token (emitido con `aud` = client id de la SPA, que no tiene
 * appRoles propios) siempre trae `roles` vacio sin importar el rol real
 * asignado al usuario. Confirmado contra los app registrations reales
 * (`az ad app show` sobre "Panel Admin SPA" -- `appRoles: []`).
 *
 * Mientras se resuelve el access token, devuelve `[]` (fail-closed): las
 * acciones gateadas quedan ocultas hasta confirmar el rol, nunca se
 * muestran de mas.
 */
export function useAccountRoles(): readonly AdminRole[] {
  const { accounts } = useMsal();
  const account = accounts[0];
  const [roles, setRoles] = useState<readonly AdminRole[]>([]);

  useEffect(() => {
    if (!account) {
      setRoles([]);
      return;
    }

    let cancelled = false;

    void msalInstance
      .acquireTokenSilent({
        ...apiRequest,
        account,
        redirectUri: silentAuthRedirectUri,
      })
      .then((result) => {
        if (!cancelled) {
          setRoles(extractRoles(decodeJwtPayload(result.accessToken)));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRoles([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [account]);

  return roles;
}
